import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';
import { ScholarshipDetail } from '@common-types';
import { omit } from 'lodash-es';

import { Container } from '@components/Layout/components/Container';

import { APPLICATION_REQUIREMENTS_LABELS, MILITARY_LABELS } from '@constants/scholarshipDetail';

import EllipseIcon from '@images/ellipse.svg?react';
import InfoIcon from '@images/info.svg?react';

import { sxStyles, StyledCategoryItem } from './Requirements.styles';

const renderCategoryItem = (fieldName: string, value: boolean | string | number, tooltipText?: string) => {
  if (!value) return '';

  return (
    <StyledCategoryItem key={fieldName}>
      <CoreBox>
        <EllipseIcon />
      </CoreBox>

      <CoreTypography>{fieldName}</CoreTypography>

      {tooltipText && (
        <CoreTooltip title={tooltipText}>
          <CoreBox sx={sxStyles.tooltipIcon}>
            <InfoIcon />
          </CoreBox>
        </CoreTooltip>
      )}
    </StyledCategoryItem>
  );
};

interface RequirementsProps {
  scholarship: ScholarshipDetail;
}

export const Requirements = (props: RequirementsProps) => {
  const { scholarship } = props;

  const omittedApplicationRequirements = omit(scholarship?.applicationRequirements, ['special', 'essayPrompt']);

  const hasApplicationRequirements = Object.values(omittedApplicationRequirements ?? {}).some((item) => !!item);
  const hasMilitaryService = Object.values(scholarship?.militaryService ?? {})?.some((item) => item);
  const hasScoreRequirements = Object.values(scholarship?.scoreRequirements ?? {}).some((item) => item);

  if (!hasApplicationRequirements && !hasMilitaryService && !hasScoreRequirements) return null;

  return (
    <CoreBox sx={sxStyles.bgColor}>
      <Container sx={sxStyles.container}>
        <CoreTypography sx={sxStyles.sectionTitle} variant="h4" component="h3">
          Requirements
        </CoreTypography>

        <CoreBox sx={sxStyles.categoriesWrap}>
          {hasApplicationRequirements && (
            <CoreBox>
              <CoreTypography sx={sxStyles.categoryTitle} variant="overline" component="h5">
                Application
              </CoreTypography>

              <CoreBox sx={sxStyles.categoryItemsWrap}>
                {Object?.entries(omittedApplicationRequirements)?.map(([key, value]) => {
                  if (key === 'essay' && value) {
                    return renderCategoryItem(
                      APPLICATION_REQUIREMENTS_LABELS[key],
                      value,
                      scholarship?.applicationRequirements.essayPrompt,
                    );
                  }
                  if (key === 'other' && value) {
                    return renderCategoryItem(APPLICATION_REQUIREMENTS_LABELS[key], value, value as string);
                  }

                  return renderCategoryItem(APPLICATION_REQUIREMENTS_LABELS[key], value);
                })}
              </CoreBox>
            </CoreBox>
          )}

          {hasMilitaryService && (
            <CoreBox>
              <CoreTypography sx={sxStyles.categoryTitle} variant="overline" component="h5">
                Military
              </CoreTypography>

              <CoreBox sx={sxStyles.categoryItemsWrap}>
                {Object?.entries(scholarship?.militaryService)?.map(([key, value]) =>
                  renderCategoryItem(MILITARY_LABELS[key], value),
                )}
              </CoreBox>
            </CoreBox>
          )}

          {hasScoreRequirements && (
            <CoreBox>
              <CoreTypography sx={sxStyles.categoryTitle} variant="overline" component="h5">
                GPA/test Score
              </CoreTypography>

              <CoreBox sx={sxStyles.categoryItemsWrap}>
                {renderCategoryItem(
                  `Min GPA on a 4.0 scale: ${scholarship?.scoreRequirements?.minimumGpa}`,
                  scholarship?.scoreRequirements?.minimumGpa ?? '',
                )}

                {renderCategoryItem(
                  `Min SAT test score: ${scholarship?.scoreRequirements?.minimumSat}`,
                  scholarship?.scoreRequirements?.minimumSat ?? '',
                )}

                {renderCategoryItem(
                  `Min ACT test score: ${scholarship?.scoreRequirements?.minimumAct}`,
                  scholarship?.scoreRequirements?.minimumAct ?? '',
                )}
              </CoreBox>
            </CoreBox>
          )}
        </CoreBox>
      </Container>
    </CoreBox>
  );
};
